import { Capacitor } from "@capacitor/core";
import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useRef,
} from "react";
import axiosInstance, { API_ENDPOINTS } from "@/config/api";
import { isTVDevice } from "@/utils/tvDevice";
import { fetchAuthWithFallback } from "@/utils/authRequest";
import {
    ACCOUNT_AUTH_LOSS_REDIRECT_KEY,
    redirectToLandingAfterAuthLoss,
} from "@/utils/authLossRedirect";
import { getRetryAfterSecondsFromHeaders } from "@/utils/rateLimit";
import { resetQuickStartModalDismissal } from "@/utils/quickStartModal";
const REFRESH_LOCK_KEY = "qit:auth-refresh-lock";
const REFRESH_LOCK_TTL_MS = 15_000;
const REFRESH_LOCK_WAIT_TIMEOUT_MS = 20_000;
const LAST_ACTIVITY_KEY = "qit:last-auth-activity";
const AUTH_INACTIVITY_TIMEOUT_MS = 12 * 60 * 60 * 1000;
const USER_PROFILE_SYNC_INTERVAL_MS = 5 * 60 * 1000;
const AUTH_STORAGE_KEYS = new Set(["user", "authToken", "refreshToken", REFRESH_LOCK_KEY]);

type RefreshLockState = {
    owner: string;
    expiresAt: number;
};

const readRefreshLock = (): RefreshLockState | null => {
    if (typeof window === "undefined") return null;

    try {
        const raw = window.localStorage.getItem(REFRESH_LOCK_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<RefreshLockState>;
        if (!parsed || typeof parsed.owner !== "string" || typeof parsed.expiresAt !== "number") {
            return null;
        }
        return parsed as RefreshLockState;
    } catch {
        return null;
    }
};

const tryAcquireRefreshLock = (owner: string): boolean => {
    if (typeof window === "undefined") return true;

    const now = Date.now();
    const currentLock = readRefreshLock();
    if (currentLock && currentLock.owner !== owner && currentLock.expiresAt > now) {
        return false;
    }

    const nextLock: RefreshLockState = {
        owner,
        expiresAt: now + REFRESH_LOCK_TTL_MS,
    };

    try {
        window.localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(nextLock));
        const confirmedLock = readRefreshLock();
        return confirmedLock?.owner === owner;
    } catch {
        return true;
    }
};

const releaseRefreshLock = (owner: string): void => {
    if (typeof window === "undefined") return;

    const currentLock = readRefreshLock();
    if (currentLock?.owner === owner) {
        window.localStorage.removeItem(REFRESH_LOCK_KEY);
    }
};

export interface User {
    id: string;
    name: string;               // mapped from backend user_name
    email: string;
    avatar?: string;
    phoneNumber?: string;
    role: "admin" | "user";
    sessionId?: string;
    subscriptionPlan?: "basic" | "pro" | "venue" | "free" | null;
}

interface UserContextType {
    user: User | null;
    loading: boolean;
    authToken: string | null;
    refreshToken: string | null;
    login: (userData: User, tokens: { access: string; refresh: string }) => void;
    logout: (redirectTo?: string) => void;
    clearAuthState?: () => void;
    refreshAuth: () => Promise<boolean>;
    refreshUser: () => Promise<void>;
    fetchSubscription: (paymentId: string) => Promise<void>;
    syncAfterPayment: () => Promise<void>;
    updateAvatar: (avatar: string | null) => Promise<User | null>;
    updateUsername: (username: string) => Promise<User | null>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const normalizeStoredUser = (value: any): User | null => {
    if (!value || typeof value !== "object") return null;

    const id = String(value.id ?? "").trim();
    const email = String(value.email ?? "").trim();
    const name = String(value.name ?? value.user_name ?? "").trim();

    if (!id || !email || !name) return null;

    return {
        id,
        name,
        email,
        role: value.role === "admin" ? "admin" : "user",
        avatar: value.avatar ?? value.avatar_url ?? value.avatarUrl ?? undefined,
        phoneNumber: value.phoneNumber ?? value.phone_number ?? undefined,
        sessionId: value.sessionId ?? undefined,
        subscriptionPlan: value.subscriptionPlan ?? null,
    };
};

const decodeBase64Url = (value: string): string => {
    const normalized = String(value || "").replace(/-/g, "+").replace(/_/g, "/");
    const paddingLength = (4 - (normalized.length % 4)) % 4;
    const padded = normalized.padEnd(normalized.length + paddingLength, "=");
    return atob(padded);
};

const decodeJwtPayload = (token: string | null): Record<string, unknown> | null => {
    if (!token) return null;

    try {
        const parts = token.split(".");
        if (parts.length !== 3) return null;

        const payload = JSON.parse(decodeBase64Url(parts[1]));
        if (!payload || typeof payload !== "object") return null;
        return payload as Record<string, unknown>;
    } catch (error) {
        console.error("Token decode error:", error);
        return null;
    }
};

const isTokenValidForAuthState = (token: string | null): boolean => {
    if (!token) return false;

    try {
        const payload = decodeJwtPayload(token);
        if (!payload) return false;
        const expSeconds = Number(payload.exp);
        if (!Number.isFinite(expSeconds)) return false;
        const exp = expSeconds * 1000;
        const now = Date.now();

        return exp > now + 30000;
    } catch (e) {
        console.error("Token validation error:", e);
        return false;
    }
};

const readLastAuthActivity = (): number => {
    if (typeof window === "undefined") return Date.now();

    const parsed = Number(window.localStorage.getItem(LAST_ACTIVITY_KEY));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : Date.now();
};

const stampAuthActivity = (): void => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
};

const isPersistentAuthSession = (): boolean =>
    Capacitor.isNativePlatform() || (typeof window !== "undefined" && (/^\/tv(?:\/|$)/.test(window.location.pathname) || isTVDevice()));

const isAuthIdleExpired = (): boolean => {
    if (isPersistentAuthSession()) return false;
    return Date.now() - readLastAuthActivity() >= AUTH_INACTIVITY_TIMEOUT_MS;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [authToken, setAuthToken] = useState<string | null>(null);
    const [refreshToken, setRefreshToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    // ⭐ ADDED: Simple refresh tracking to prevent hitting backend limits
    const lastRefreshTime = useRef<number>(0);
    const refreshCount = useRef<number>(0);
    const refreshCountResetTime = useRef<number>(Date.now());
    const refreshInFlight = useRef<Promise<boolean> | null>(null);
    const tvStartupRefreshAttempted = useRef(false);
    const authGeneration = useRef(0);
    const refreshTabId = useRef(`tab-${Math.random().toString(36).slice(2, 10)}`);

    const persistUserState = useCallback((nextUser: User | null) => {
        if (nextUser) {
            localStorage.setItem("user", JSON.stringify(nextUser));
        } else {
            localStorage.removeItem("user");
        }

        if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("qit:user-updated"));
        }
    }, []);

    /** ✔ Fetch subscription via paymentId */
    const fetchSubscription = useCallback(async (paymentId: string) => {
        try {
            const res = await axiosInstance.get(`/api/v1/payments/payment/${paymentId}`);
            const planName = res?.data?.plan?.name;

            if (!planName) return;

            const updatedUser = {
                ...(user ?? {}),     // ⭐ allow updating even if user is null
                subscriptionPlan: planName.toLowerCase(),
            };

            setUser(updatedUser as User);
            persistUserState(updatedUser as User);
            console.log("⭐ Subscription updated:", updatedUser.subscriptionPlan);
        } catch (err) {
            console.error("❌ fetchSubscription error:", err);
        }
    }, [persistUserState, user]);

    const clearAuthState = useCallback(() => {
        authGeneration.current += 1;
        tvStartupRefreshAttempted.current = false;
        resetQuickStartModalDismissal();
        setUser(null);
        setAuthToken(null);
        setRefreshToken(null);
        persistUserState(null);
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("guestUser");
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        localStorage.removeItem("lastSessionURL");
        localStorage.removeItem("lastPlaylistId");
        localStorage.removeItem("currentPlaylist");
        localStorage.removeItem("currentSession");
        localStorage.removeItem("current_session_id");
        localStorage.removeItem("current_session_info");
        localStorage.removeItem("sessionInfo");
        localStorage.removeItem("session_invite_token");
        localStorage.removeItem("invite_token");
        localStorage.removeItem("playlist_added_songs");
        localStorage.removeItem("userBalance");
        // Remove user-scoped playlist caches.
        Object.keys(localStorage).forEach((key) => {
            if (
                key.startsWith("currentPlaylist:") ||
                key.startsWith("playlist_added_songs:") ||
                key.startsWith("session:") ||
                key.startsWith("suggestions_") ||
                key.startsWith("suggestions-cache-")
            ) {
                localStorage.removeItem(key);
            }
        });
        delete axiosInstance.defaults.headers.common['Authorization'];
        refreshCount.current = 0;
        refreshCountResetTime.current = Date.now();
        refreshInFlight.current = null;
        releaseRefreshLock(refreshTabId.current);
    }, [persistUserState]);

    const clearAuthStateAndRedirect = useCallback(() => {
        clearAuthState();
        redirectToLandingAfterAuthLoss();
    }, [clearAuthState]);

    /** Load stored Login */
    useEffect(() => {
      try {
        const storedUser = localStorage.getItem("user");
        const storedAccess = localStorage.getItem("authToken");
        const storedRefresh = localStorage.getItem("refreshToken");
        const hasValidAccess = isTokenValidForAuthState(storedAccess);
        const hasValidRefresh = isTokenValidForAuthState(storedRefresh);

        if ((storedUser && !storedAccess && !(isPersistentAuthSession() && hasValidRefresh)) || ((storedUser || storedAccess) && !hasValidAccess && !hasValidRefresh)) {
            console.warn("Clearing stale auth state because refresh token is missing or invalid.");
            clearAuthStateAndRedirect();
            return;
        }

        if (storedAccess && hasValidAccess) {
            setAuthToken(storedAccess);
        }
        if (storedRefresh && hasValidRefresh) {
            setRefreshToken(storedRefresh);
        }
        if (storedUser) {
            try {
                const parsedUser = JSON.parse(storedUser);
                const normalizedUser = normalizeStoredUser(parsedUser);
                if (normalizedUser && (hasValidAccess || hasValidRefresh)) {
                    setUser(normalizedUser);
                    persistUserState(normalizedUser);
                } else {
                    localStorage.removeItem("user");
                }
            } catch (error) {
                console.warn("Failed to parse stored user; clearing invalid value.", error);
                localStorage.removeItem("user");
            }
        }

      } catch (error) {
        console.warn("Browser storage is unavailable; continuing without a saved session.", error);
      } finally {
        setLoading(false);
      }
    }, [clearAuthStateAndRedirect, persistUserState]);

    /** Logout */
    const logout = useCallback((redirectTo = "/") => {
        clearAuthState();
        // An explicit logout abandons any pending QR/auth navigation intent.
        // Automatic auth cleanup intentionally preserves these keys so Google
        // signup can return the user to the scanned session.
        sessionStorage.removeItem("qit:pending-auth-redirect");
        sessionStorage.removeItem("qit:pending-qr-session");
        window.location.href = redirectTo;
    }, [clearAuthState]);

    /** ⭐ ENHANCED: Check if token is expired or about to expire */
    const isTokenValid = useCallback((token: string | null): boolean => {
        return isTokenValidForAuthState(token);
    }, []);

    /** ⭐ ENHANCED: Get valid token from multiple sources */
    const getValidRefreshToken = useCallback((): string | null => {
        // Check state first
        if (refreshToken && isTokenValid(refreshToken)) {
            return refreshToken;
        }

        // Check localStorage
        const storedToken = localStorage.getItem("refreshToken");
        if (storedToken && isTokenValid(storedToken)) {
            // Sync state if localStorage has valid token
            setRefreshToken(storedToken);
            return storedToken;
        }

        return null;
    }, [refreshToken, isTokenValid]);

    const syncAuthStateFromStorage = useCallback((): boolean => {
        if (typeof window === "undefined") return false;

        const storedUser = localStorage.getItem("user");
        const storedAccess = localStorage.getItem("authToken");
        const storedRefresh = localStorage.getItem("refreshToken");
        const hasValidAccess = isTokenValidForAuthState(storedAccess);
        const hasValidRefresh = isTokenValid(storedRefresh);
        const hadAccountState = Boolean(user || storedUser || authToken || storedAccess || refreshToken || storedRefresh);

        let synced = false;

        if (hadAccountState && ((!storedAccess && !(isPersistentAuthSession() && hasValidRefresh)) || (!hasValidAccess && !hasValidRefresh))) {
            clearAuthStateAndRedirect();
            return false;
        }

        if (storedAccess && hasValidAccess) {
            setAuthToken(storedAccess);
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${storedAccess}`;
            synced = true;
        } else {
            setAuthToken(null);
            delete axiosInstance.defaults.headers.common['Authorization'];
        }

        if (storedRefresh && hasValidRefresh) {
            setRefreshToken(storedRefresh);
        } else {
            setRefreshToken(null);
        }

        if (storedUser) {
            try {
                const parsedUser = JSON.parse(storedUser);
                const normalizedUser = normalizeStoredUser(parsedUser);
                if (normalizedUser && (hasValidAccess || hasValidRefresh)) {
                    setUser(normalizedUser);
                } else {
                    setUser(null);
                }
            } catch (error) {
                console.warn("Failed to parse stored user during auth sync.", error);
                setUser(null);
            }
        } else if (!hasValidAccess && !hasValidRefresh) {
            setUser(null);
        }

        return synced;
    }, [authToken, clearAuthStateAndRedirect, isTokenValid, refreshToken, user]);

    useEffect(() => {
        const syncAuthState = (event: StorageEvent) => {
            if (event.key && !AUTH_STORAGE_KEYS.has(event.key)) {
                return;
            }
            syncAuthStateFromStorage();
        };

        window.addEventListener("storage", syncAuthState);
        return () => window.removeEventListener("storage", syncAuthState);
    }, [syncAuthStateFromStorage]);

    const waitForRefreshFromAnotherTab = useCallback(async (): Promise<boolean> => {
        if (typeof window === "undefined") {
            return syncAuthStateFromStorage();
        }

        return await new Promise<boolean>((resolve) => {
            let settled = false;
            let expiryCheckTimeoutId: number | null = null;

            const finish = (value: boolean) => {
                if (settled) return;
                settled = true;
                window.clearTimeout(timeoutId);
                if (expiryCheckTimeoutId !== null) {
                    window.clearTimeout(expiryCheckTimeoutId);
                }
                window.removeEventListener("storage", handleStorageChange);
                resolve(value);
            };

            const trySync = () => {
                const currentLock = readRefreshLock();
                if (!currentLock || currentLock.expiresAt <= Date.now()) {
                    finish(syncAuthStateFromStorage());
                }
            };

            const scheduleExpiryCheck = () => {
                if (expiryCheckTimeoutId !== null) {
                    window.clearTimeout(expiryCheckTimeoutId);
                    expiryCheckTimeoutId = null;
                }

                const currentLock = readRefreshLock();
                if (!currentLock) {
                    return;
                }

                const msUntilExpiry = Math.max(0, currentLock.expiresAt - Date.now());
                expiryCheckTimeoutId = window.setTimeout(() => {
                    trySync();
                }, Math.min(msUntilExpiry + 25, REFRESH_LOCK_WAIT_TIMEOUT_MS));
            };

            const handleStorageChange = (event: StorageEvent) => {
                if (event.key && !AUTH_STORAGE_KEYS.has(event.key)) {
                    return;
                }
                scheduleExpiryCheck();
                trySync();
            };

            const timeoutId = window.setTimeout(() => {
                finish(syncAuthStateFromStorage());
            }, REFRESH_LOCK_WAIT_TIMEOUT_MS);

            window.addEventListener("storage", handleStorageChange);
            scheduleExpiryCheck();
            trySync();
        });
    }, [syncAuthStateFromStorage]);

    /** Refresh JWT - ENHANCED VERSION */
    const refreshAuth = useCallback(async (): Promise<boolean> => {
        if (refreshInFlight.current) {
            return refreshInFlight.current;
        }

        const refreshPromise = (async (): Promise<boolean> => {
            const now = Date.now();
            const generation = authGeneration.current;

            // ⭐ ADDED: Rate limiting protection (backend has @limiter.limit("10/minute"))
            if (now - refreshCountResetTime.current > 60000) { // Reset after 1 minute
                refreshCount.current = 0;
                refreshCountResetTime.current = now;
            }

            // ⭐ ADDED: Prevent hitting backend's 10/minute limit
            if (refreshCount.current >= 9) { // Leave 1 for emergency
                console.log("⏳ Approaching backend rate limit (9/10), waiting...");

                // Wait for rate limit to reset
                const timeToWait = 60000 - (now - refreshCountResetTime.current);
                if (timeToWait > 0) {
                    console.log(`Waiting ${Math.ceil(timeToWait / 1000)} seconds...`);
                    return false;
                }
            }

            // ⭐ ADDED: Cooldown between refreshes (min 1 second)
            if (now - lastRefreshTime.current < 1000) {
                console.log("⏳ Too soon since last refresh (cooldown)");
                return false;
            }

            // Use enhanced token getter before coordinating across tabs.
            const validRefreshToken = getValidRefreshToken();
            if (!validRefreshToken) {
                console.log("❌ No valid refresh token available");
                clearAuthStateAndRedirect();
                return false;
            }

            const existingLock = readRefreshLock();
            if (existingLock && existingLock.owner !== refreshTabId.current && existingLock.expiresAt > now) {
                console.log("⏳ Another tab is already refreshing auth, waiting...");
                return waitForRefreshFromAnotherTab();
            }

            if (!tryAcquireRefreshLock(refreshTabId.current)) {
                console.log("⏳ Another tab acquired the refresh lock first, waiting...");
                return waitForRefreshFromAnotherTab();
            }

            lastRefreshTime.current = now;
            refreshCount.current++;

            console.log(`🔄 Refresh attempt #${refreshCount.current}/minute`);

            try {
                console.log("📡 Sending refresh request...");

                const res = await fetchAuthWithFallback("refresh", {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${validRefreshToken}`,
                        "Content-Type": "application/json"
                    }
                });

                if (generation !== authGeneration.current) return false;
                console.log("📥 Refresh response status:", res.status);

                if (!res.ok) {
                    console.error(`❌ Refresh failed with status: ${res.status}`);
                    const errorText = await res.text();
                    console.error("Error response:", errorText);

                    // If refresh token is invalid/expired (401)
                    if (res.status === 401) {
                        console.log("🔒 Refresh token expired or invalid");
                        clearAuthStateAndRedirect();
                        return false;
                    }

                    // ⭐ ADDED: Handle 429 Rate Limit response
                    if (res.status === 429) {
                        const retryAfterSeconds = getRetryAfterSecondsFromHeaders(res.headers) ?? 60;
                        console.log(`⏳ Backend rate limit hit (429), waiting ${retryAfterSeconds} seconds`);
                        refreshCount.current = 10; // Force wait
                        refreshCountResetTime.current = Date.now() - 60000 + retryAfterSeconds * 1000;
                        return false;
                    }

                    // If wrong token type (400) - you're sending access token instead of refresh token
                    if (res.status === 400) {
                        console.log("⚠️ Likely sent access token instead of refresh token");
                        // Debug what token we're storing as "refresh"
                        try {
                            const payload = decodeJwtPayload(validRefreshToken);
                            if (payload) {
                                const expSeconds = Number(payload.exp);
                                const iatSeconds = Number(payload.iat);
                                console.log("Token payload:", {
                                    exp: Number.isFinite(expSeconds) ? new Date(expSeconds * 1000).toISOString() : "unknown",
                                    iat: Number.isFinite(iatSeconds) ? new Date(iatSeconds * 1000).toISOString() : "unknown",
                                    sub: payload.sub
                                });
                            }
                        } catch (e) {
                            console.error("Could not decode token:", e);
                        }
                    }

                    return false;
                }

                const data = await res.json();
                if (generation !== authGeneration.current) return false;

                if (!data.access_token) {
                    console.error("❌ Backend did not return access token:", data);
                    return false;
                }

                console.log("✅ Token refreshed successfully");
                console.log("🔍 Response includes refresh_token?", !!data.refresh_token);

                // ⭐ ADDED: Debug token rotation
                if (data.refresh_token) {
                    const oldTokenStart = validRefreshToken.substring(0, 20);
                    const newTokenStart = data.refresh_token.substring(0, 20);
                    console.log("🔄 Token rotation:", {
                        oldTokenStart,
                        newTokenStart,
                        isDifferent: oldTokenStart !== newTokenStart
                    });
                }

                // Update state
                setAuthToken(data.access_token);
                localStorage.setItem("authToken", data.access_token);

                // ✅ CRITICAL: Update axios instance headers
                axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;

                // Also update refresh token if new one provided (refresh token rotation)
                if (data.refresh_token) {
                    setRefreshToken(data.refresh_token);
                    localStorage.setItem("refreshToken", data.refresh_token);
                    console.log("🔄 Refresh token rotated");
                }

                // ⭐ ADDED: Reset usage counter on successful refresh
                refreshCount.current = 0;
                refreshCountResetTime.current = Date.now();

                return true;
            } catch (err) {
                if (generation !== authGeneration.current) return false;
                console.error("❌ Refresh error:", err);
                // TV engines report transient failures using different error messages.
                // Keep the saved login unless the server explicitly rejects refresh.
                if (isPersistentAuthSession()) return false;

                // Don't logout immediately on network errors
                if (err.message?.includes("Failed to fetch") || err.message?.includes("NetworkError")) {
                    console.log("🌐 Network error - will retry later");
                    return false;
                }

                console.log("🔒 Auth error - logging out");
                setTimeout(() => clearAuthStateAndRedirect(), 2000);
                return false;
            } finally {
                if (generation === authGeneration.current) releaseRefreshLock(refreshTabId.current);
            }
        })();

        refreshInFlight.current = refreshPromise;

        try {
            return await refreshPromise;
        } finally {
            if (refreshInFlight.current === refreshPromise) {
                refreshInFlight.current = null;
            }
        }
    }, [clearAuthStateAndRedirect, getValidRefreshToken, waitForRefreshFromAnotherTab]);

    /** Backup method if Bearer header doesn't work */
    const tryAlternativeRefresh = async (token: string, deviceId: string): Promise<boolean> => {
        try {
            console.log("🔄 Trying refresh with body parameter (fallback)...");
            const res = await fetchAuthWithFallback("refresh", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    refresh_token: token,
                    device_id: deviceId
                }),
            });

            if (!res.ok) {
                const errorText = await res.text();
                console.error("Alternative method failed:", errorText);
                return false;
            }

            const data = await res.json();
            if (!data.access_token) return false;

            // Update everything
            setAuthToken(data.access_token);
            localStorage.setItem("authToken", data.access_token);
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;

            if (data.refresh_token) {
                setRefreshToken(data.refresh_token);
                localStorage.setItem("refreshToken", data.refresh_token);
            }

            console.log("✅ Token refreshed via alternative method");
            return true;
        } catch (err) {
            console.error("Alternative method error:", err);
            return false;
        }
    };

    // Keep TV credentials fresh even while no remote activity occurs.
    useEffect(() => {
        if (!authToken && !(isPersistentAuthSession() && refreshToken)) return;

        const validRefreshToken = getValidRefreshToken();
        if (!validRefreshToken) return;

        const interval = setInterval(() => {
            if (isAuthIdleExpired()) {
                console.warn("Clearing auth state after 12 hours without activity.");
                clearAuthStateAndRedirect();
                return;
            }
            console.log("🔄 Auto-refreshing token (scheduled)...");
            refreshAuth();
        }, 14 * 60 * 1000); // Rotate credentials every 14 minutes, including on unattended TVs.

        return () => clearInterval(interval);
    }, [authToken, refreshToken, clearAuthStateAndRedirect, getValidRefreshToken, refreshAuth]);

    // ⭐ ENHANCED: Add axios interceptor with better error handling
    useEffect(() => {
        const interceptor = axiosInstance.interceptors.response.use(
            response => response,
            async error => {
                const originalRequest = error.config;
                const requestUrl = String(originalRequest?.url || "");
                const requestPath = requestUrl.split("?")[0];
                const isJoinEndpoint =
                    /\/api\/v1\/sessions\/[^/]+\/join$/.test(requestPath) ||
                    /\/api\/v1\/sessions\/join\/token$/.test(requestPath);
                const skipAuthRefresh = Boolean((originalRequest as any)?.skipAuth);

                // Join flows handle guest/auth fallback in the caller.
                if (error.response?.status === 401 && (isJoinEndpoint || skipAuthRefresh)) {
                    return Promise.reject(error);
                }

                // If 401 error and not already retried
                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    console.log("🔧 Axios caught 401 - attempting token refresh...");
                    const refreshed = await refreshAuth();

                    if (refreshed) {
                        // Update token in the request
                        const newToken = localStorage.getItem('authToken');
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        return axiosInstance(originalRequest);
                    } else {
                        const stillHasRefreshToken = Boolean(getValidRefreshToken());
                        if (!stillHasRefreshToken) {
                            // Permanent auth failure path
                            console.log("🔒 Refresh failed and no valid refresh token remains. Logging out...");
                            setTimeout(() => clearAuthStateAndRedirect(), 1000);
                        } else {
                            // Transient failure path (e.g. background/resume/network/rate-limit cooldown)
                            // Keep session state intact and let subsequent requests retry refresh.
                            console.warn("⚠️ Refresh attempt failed transiently; preserving auth/session state.");
                        }
                    }
                }

                // Do not automatically replay rate-limited requests. Retrying every
                // mounted GET keeps the server-side bucket hot and makes the app
                // appear stuck in a permanent 429 loop.
                if (error.response?.status === 429) {
                    const retryAfterSeconds = getRetryAfterSecondsFromHeaders(error.response?.headers);
                    const cooldownSeconds = retryAfterSeconds && retryAfterSeconds > 0 ? retryAfterSeconds : 60;
                    console.warn(
                        `Rate limited (${originalRequest?.url || "request"}). ` +
                        `Waiting for user-initiated refresh; retry after ${cooldownSeconds}s.`
                    );
                }

                return Promise.reject(error);
            }
        );

        return () => {
            axiosInstance.interceptors.response.eject(interceptor);
        };
    }, [clearAuthStateAndRedirect, getValidRefreshToken, refreshAuth]);

    // Also refresh when token is about to expire (proactive refresh)
    useEffect(() => {
        if (!authToken && !(isPersistentAuthSession() && refreshToken)) return;

        const checkTokenExpiry = (forceTVRefresh = false) => {
            try {
                if (isAuthIdleExpired()) {
                    console.warn("Clearing auth state after 12 hours without activity.");
                    clearAuthStateAndRedirect();
                    return;
                }

                const validRefreshToken = getValidRefreshToken();
                if (!validRefreshToken) {
                    console.warn("Clearing stale auth state because no valid refresh token is available.");
                    clearAuthStateAndRedirect();
                    return;
                }

                if (isPersistentAuthSession() && (forceTVRefresh || !tvStartupRefreshAttempted.current || !authToken)) {
                    tvStartupRefreshAttempted.current = true;
                    void refreshAuth();
                    return;
                }

                if (!localStorage.getItem("authToken")) {
                    console.warn("Access token is missing; attempting refresh before clearing auth state.");
                    refreshAuth();
                    return;
                }

                const payload = decodeJwtPayload(authToken);
                if (!payload) return;
                const expSeconds = Number(payload.exp);
                if (!Number.isFinite(expSeconds)) return;
                const exp = expSeconds * 1000;
                const now = Date.now();
                const timeUntilExpiry = exp - now;

                // If token expires in less than 5 minutes, refresh it
                if (timeUntilExpiry < 5 * 60 * 1000) {
                    console.log("🔄 Token expiring soon - refreshing proactively...");
                    refreshAuth();
                }
            } catch (err) {
                console.error("❌ Error checking token expiry:", err);
            }
        };

        // Check every minute
        const interval = setInterval(checkTokenExpiry, 60 * 1000);
        checkTokenExpiry(); // Initial check

        const handleWindowFocus = () => {
            checkTokenExpiry(isPersistentAuthSession());
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                checkTokenExpiry(isPersistentAuthSession());
            }
        };

        window.addEventListener("focus", handleWindowFocus);
        window.addEventListener("pageshow", handleWindowFocus);
        window.addEventListener("online", handleWindowFocus);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            clearInterval(interval);
            window.removeEventListener("focus", handleWindowFocus);
            window.removeEventListener("pageshow", handleWindowFocus);
            window.removeEventListener("online", handleWindowFocus);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [authToken, refreshToken, clearAuthStateAndRedirect, getValidRefreshToken, refreshAuth]);

    useEffect(() => {
        if (!authToken) return;

        stampAuthActivity();
        let lastStampAt = Date.now();
        const activityEvents = ["pointerdown", "keydown", "touchstart", "scroll", "visibilitychange"];
        const handleActivity = () => {
            if (document.visibilityState === "hidden") return;
            const now = Date.now();
            if (now - lastStampAt < 30_000) return;
            lastStampAt = now;
            stampAuthActivity();
        };

        activityEvents.forEach((eventName) => {
            window.addEventListener(eventName, handleActivity, { passive: true });
        });

        return () => {
            activityEvents.forEach((eventName) => {
                window.removeEventListener(eventName, handleActivity);
            });
        };
    }, [authToken]);

    /** Login */
    const login = useCallback((userData: User, tokens: { access: string; refresh: string }) => {
        authGeneration.current += 1;
        tvStartupRefreshAttempted.current = isPersistentAuthSession();
        sessionStorage.removeItem(ACCOUNT_AUTH_LOSS_REDIRECT_KEY);
        setUser(userData);
        setAuthToken(tokens.access);
        setRefreshToken(tokens.refresh);

        persistUserState(userData);
        localStorage.setItem("authToken", tokens.access);
        localStorage.setItem("refreshToken", tokens.refresh);
        stampAuthActivity();

        // Set axios default headers for immediate use
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${tokens.access}`;

        // ⭐ ADDED: Reset refresh counter on login
        refreshCount.current = 0;
        refreshCountResetTime.current = Date.now();

        console.log("✅ User logged in:", userData.email);
    }, [persistUserState]);

    /** Refresh user from auth-service */
    const refreshUser = useCallback(async () => {
        if (!authToken || !localStorage.getItem("authToken")) return;
        const generation = authGeneration.current;

        try {
            const res = await axiosInstance.get(API_ENDPOINTS.auth.currentUser);
            if (generation !== authGeneration.current) return;

            const backend = res.data;
            const updated: User = {
                id: String(backend.id ?? user?.id ?? ""),
                name: String(backend.user_name ?? backend.name ?? user?.name ?? "").trim(),
                email: String(backend.email ?? user?.email ?? "").trim(),
                role: backend.role,
                avatar: backend.avatar ?? backend.avatar_url,
                subscriptionPlan: user?.subscriptionPlan ?? null,
            };

            setUser(updated);
            persistUserState(updated);
            console.log("🔄 User refreshed:", updated);
        } catch (err) {
            console.error("❌ refreshUser failed:", err);
        }
    }, [authToken, persistUserState, user?.email, user?.id, user?.name, user?.subscriptionPlan]);

    // localStorage makes the profile available immediately, but it is only a cache.
    // Reconcile it with the account service so avatar changes made on another
    // browser/device propagate to every signed-in session.
    useEffect(() => {
        if (!authToken) return;

        let lastSyncAt = 0;
        const syncProfile = () => {
            if (document.visibilityState === "hidden") return;
            const now = Date.now();
            if (now - lastSyncAt < 5_000) return;
            lastSyncAt = now;
            void refreshUser();
        };

        syncProfile();
        const intervalId = window.setInterval(syncProfile, USER_PROFILE_SYNC_INTERVAL_MS);
        window.addEventListener("focus", syncProfile);
        document.addEventListener("visibilitychange", syncProfile);

        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener("focus", syncProfile);
            document.removeEventListener("visibilitychange", syncProfile);
        };
    }, [authToken, refreshUser]);

    const updateAvatar = useCallback(async (avatar: string | null): Promise<User | null> => {
        if (!authToken || !user) return null;

        const response = await axiosInstance.put(API_ENDPOINTS.auth.updateAvatar, {
            avatar_url: avatar,
        });

        const backendUser = response?.data?.user ?? {};
        const nextUser: User = {
            ...user,
            id: String(backendUser.id ?? user.id),
            name: backendUser.user_name ?? backendUser.name ?? user.name,
            email: backendUser.email ?? user.email,
            role: backendUser.role ?? user.role,
            avatar: backendUser.avatar ?? backendUser.avatar_url ?? avatar ?? undefined,
            subscriptionPlan: user.subscriptionPlan ?? null,
        };

        setUser(nextUser);
        persistUserState(nextUser);

        if (response?.data?.preferences) {
            localStorage.setItem("userPreferences", JSON.stringify(response.data.preferences));
        }

        return nextUser;
    }, [authToken, persistUserState, user]);

    const updateUsername = useCallback(async (username: string): Promise<User | null> => {
        if (!authToken || !user) return null;

        const response = await axiosInstance.put(API_ENDPOINTS.auth.updateUsername, { username });
        const backendUser = response?.data?.user ?? {};
        const nextUser: User = {
            ...user,
            name: String(backendUser.user_name ?? backendUser.name ?? username).trim(),
        };

        setUser(nextUser);
        persistUserState(nextUser);
        return nextUser;
    }, [authToken, persistUserState, user]);

    /** 🚀 Full sync after payment */
    const syncAfterPayment = useCallback(async () => {
        console.log("🔄 Running full post-payment sync...");

        try {
            // 1️⃣ Refresh auth (new access token)
            const authRefreshed = await refreshAuth();
            if (!authRefreshed) {
                console.error("❌ Auth refresh failed during post-payment sync");
                return;
            }

            // 2️⃣ Refresh user information
            await refreshUser();

            // 3️⃣ Balance refresh is handled centrally by BalanceContext.

            console.log("✅ Post-payment sync completed");
        } catch (err) {
            console.error("❌ Post-payment sync error:", err);
        }
    }, [refreshAuth, refreshUser]);

    return (
        <UserContext.Provider
            value={{
                user,
                loading,
                authToken,
                refreshToken,
                login,
                logout,
                clearAuthState,
                refreshAuth,
                refreshUser,
                fetchSubscription,
                syncAfterPayment,
                updateAvatar,
                updateUsername,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const ctx = useContext(UserContext);
    if (!ctx) throw new Error("useUser must be used inside UserProvider");
    return ctx;
};
